import React from "react";

import styled from "styled-components";

import { LicenceBlock, LicenceSection } from "@/services/licences.service";
import { colors, fonts } from "@/styles/design-system";
import { ERoutes } from "@/utils/constants";

/**
 * Phrases the document refers to that have a page of their own — linked wherever
 * they appear in the running text (case-insensitive), so a reader can jump
 * straight to the Terms of Use or their Claims Page. Opened in a new tab so the
 * licence they are reading is not lost. Internal "Section N" cross-references
 * stay plain — they point within this same document.
 */
const LINKS: ReadonlyArray<readonly [string, string]> = [
  ["Terms of Use", ERoutes.Conditions],
  ["Claims Page", ERoutes.AccountVideos],
];
const LINK_RE = new RegExp(`(${LINKS.map(([phrase]) => phrase).join("|")})`, "gi");
const hrefFor = (segment: string): string | undefined =>
  LINKS.find(([phrase]) => phrase.toLowerCase() === segment.toLowerCase())?.[1];

const DocLink = styled.a`
  color: ${colors.link.blue};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** Wrap any linkable phrase in a link; leave the rest as plain text. */
const linkify = (text: string, keyPrefix: string): React.ReactNode[] =>
  text.split(LINK_RE).map((seg, i) => {
    const href = hrefFor(seg);
    return href ? (
      <DocLink key={`${keyPrefix}-${i}`} href={href} target="_blank" rel="noopener noreferrer">
        {seg}
      </DocLink>
    ) : (
      <React.Fragment key={`${keyPrefix}-${i}`}>{seg}</React.Fragment>
    );
  });

/**
 * Renders the block model the backend produces for a YouTube Licence — the same
 * blocks the PDF is drawn from — into styled sections. Shared by the resolved-
 * claim certificate modal, the public /licence-terms page, and the pre-purchase
 * preview, so all three read identically and a wording change lands in one place.
 */

const Section = styled.section`
  & + & {
    margin-top: 44px;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${fonts.body};
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: ${colors.text.primary};
  margin: 0 0 12px;
`;

const SubHeading = styled.div`
  font-family: ${fonts.body};
  font-size: 1.08rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${colors.text.primary};
  margin: 18px 0 7px;
`;

/* The credit text: monospaced, selectable, never reflowed — this is the string
   the customer copies into their video description. */
const Verbatim = styled.pre`
  margin: 10px 0 16px;
  padding: 14px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
  line-height: 1.6;
  color: ${colors.text.primary};
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
`;

/* A label with its own lines under it — parties, track, video. The lines keep
   their breaks: an address is read, not parsed, so it gets no field names. */
const Details = styled.div`
  margin: 0 0 16px;
`;

const DetailsLabel = styled.div`
  font-family: ${fonts.body};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: ${colors.text.muted};
  margin-bottom: 5px;
`;

const DetailsLine = styled.div`
  color: ${colors.text.primary};
  line-height: 1.5;
  word-break: break-word;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  margin: 0 0 14px;

  th,
  td {
    border: 1px solid rgba(235, 194, 142, 0.18);
    padding: 8px 10px;
    text-align: left;
    vertical-align: top;
  }

  th {
    color: ${colors.text.primary};
    font-weight: 600;
    background: rgba(255, 255, 255, 0.04);
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  margin: 0 0 14px;
`;

/** Render `**bold**` runs inline; linkable phrases become links; the rest is
    plain text. */
const renderInline = (text: string): React.ReactNode =>
  text.split(/\*\*(.+?)\*\*/g).map((seg, i) =>
    i % 2 === 1 ? (
      <strong key={i}>{linkify(seg, `b${i}`)}</strong>
    ) : (
      <React.Fragment key={i}>{linkify(seg, `t${i}`)}</React.Fragment>
    ),
  );

/** A clause opening with a bold label — the label sits on its own line. */
const CLAUSE = /^\*\*(.+?)\*\*\s*(?:—\s*)?([\s\S]*)$/;
/** "(a)", "(ii)", "(3)" — an item marker rather than a clause label, so it
    stays inline instead of being stranded on a line of its own. */
const BARE_ENUMERATOR = /^\(\s*[a-z0-9]+\s*\)$/i;

const ClauseLabel = styled.div`
  font-family: ${fonts.body};
  font-weight: 700;
  letter-spacing: 0.2px;
  color: ${colors.text.primary};
  margin-bottom: 3px;
`;

/**
 * A paragraph, with its label broken onto its own line when it has one. A
 * run-in label starts the clause halfway across the line and is hard to scan
 * for; in this document every heading reads down, not across.
 */
const Paragraph: React.FC<{ text: string }> = ({ text }) => {
  const clause = text.match(CLAUSE);
  if (clause && clause[2].trim() && !BARE_ENUMERATOR.test(clause[1])) {
    return (
      <div style={{ marginBottom: 14 }}>
        <ClauseLabel>{clause[1].replace(/[.:]$/, "")}</ClauseLabel>
        <div>{renderInline(clause[2].trim())}</div>
      </div>
    );
  }
  return <div style={{ marginBottom: 12 }}>{renderInline(text)}</div>;
};

const Block: React.FC<{ block: LicenceBlock }> = ({ block }) => {
  if (Array.isArray(block)) {
    return (
      /* No bullets: each item opens with its own label, which is the marker. */
      <div style={{ margin: "0 0 4px" }}>
        {block.map((item, i) => (
          <Paragraph key={i} text={String(item)} />
        ))}
      </div>
    );
  }

  if (typeof block === "object" && block !== null && "label" in block) {
    return (
      <Details>
        <DetailsLabel>{block.label}</DetailsLabel>
        {block.lines.map((line, i) => (
          <DetailsLine key={i}>{line}</DetailsLine>
        ))}
      </Details>
    );
  }

  if (typeof block === "object" && block !== null && "pre" in block) {
    return <Verbatim>{block.pre}</Verbatim>;
  }

  if (typeof block === "object" && block !== null && "h" in block) {
    return (
      <TableScroll>
        <Table>
          {block.h.length > 0 && (
            <thead>
              <tr>{block.h.map((cell, i) => <th key={i}>{renderInline(cell)}</th>)}</tr>
            </thead>
          )}
          <tbody>
            {block.r.map((row, i) => (
              <tr key={i}>{row.map((cell, j) => <td key={j}>{renderInline(cell)}</td>)}</tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    );
  }

  const text = String(block);
  if (text.startsWith("## ")) return <SubHeading>{renderInline(text.slice(3))}</SubHeading>;
  return <Paragraph text={text} />;
};

const LicenceDocument: React.FC<{ sections: LicenceSection[] }> = ({ sections }) => (
  <>
    {sections.map((section, i) => (
      <Section key={i}>
        <SectionTitle>{section.title}</SectionTitle>
        {section.blocks.map((block, j) => (
          <Block key={j} block={block} />
        ))}
      </Section>
    ))}
  </>
);

export default LicenceDocument;
